import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { syncPayrollReadiness } from "./payrollReadinessSync";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  PayrollFrequencyEnum,
  PayrollProrationBasisEnum,
} from "@/utils/enums/payroll.enum";

/**
 * `usedByBranchCount` is present on the PAGINATED LIST only — the API counts
 * it in one grouped query per page and does not attach it to get-by-id, so it
 * is optional here rather than required.
 */
export type IPayrollPolicy = {
  usedByBranchCount?: number;
  id: number;
  companyId: number;
  policyName: string;
  payrollFrequency: PayrollFrequencyEnum;
  payrollProcessingDay: number | null;
  salaryPaymentDay: number;
  payrollPeriodFrom: number;
  payrollPeriodTo: number;
  isPeriodSameMonth: boolean;
  isAutoGenerate: boolean;
  /**
   * Optional because a policy row written before the column existed reads back
   * without it; the server defaults to CALENDAR_DAYS.
   */
  prorationBasis?: PayrollProrationBasisEnum;
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

/**
 * No `isAutoGenerate` (the server refuses turning it on — nothing generated a
 * run from it) and no `payrollProcessingDay` (only settable with it); an edit
 * leaves a stored value as it is.
 */
export interface ICreatePayrollPolicyRequest {
  policyName: string;
  payrollFrequency: PayrollFrequencyEnum;
  salaryPaymentDay: number;
  payrollPeriodFrom: number;
  payrollPeriodTo: number;
  isPeriodSameMonth: boolean;
  prorationBasis: PayrollProrationBasisEnum;
}

/** Branches a policy is assigned to — named, not just counted. */
export interface IPayrollPolicyUsage {
  linkedBranches: number;
  branches: { id: number; name: string }[];
}

export interface IGetPayrollPoliciesParams {
  page?: number;
  limit?: number;
  /** Free-text match on policy name — applied server-side. */
  search?: string;
}

export const companyPayrollPoliciesApi = createApi({
  reducerPath: "companyPayrollPoliciesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["payrollPolicies", "payrollPolicy", "payrollPolicyUsage"],
  endpoints: (builder) => ({
    /**
     * Which branches a policy drives. Editing a policy changes pay dates and
     * frequency for every branch on it, so this is read before an edit rather
     * than after a surprise.
     */
    getPayrollPolicyUsage: builder.query<
      IApiResponse<IPayrollPolicyUsage>,
      number
    >({
      query: (id) => ({
        url: `/company/settings/payroll/policies/${id}/usage`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "payrollPolicyUsage", id }],
    }),

    /**
     * Assign one policy to several branches at once. Additive — branches not
     * listed keep whatever they have.
     */
    assignPolicyToBranches: builder.mutation<
      IApiResponse<IPayrollPolicyUsage>,
      { id: number; branchIds: number[] }
    >({
      query: ({ id, branchIds }) => ({
        url: `/company/settings/payroll/policies/${id}/assign-branches`,
        method: "POST",
        body: { branchIds },
      }),
      // Branch configs change, and so does every policy's usage count.
      invalidatesTags: (_r, _e, { id }) => [
        "payrollPolicies",
        { type: "payrollPolicyUsage", id },
      ],
      onQueryStarted: syncPayrollReadiness,
    }),

    getPayrollPolicies: builder.query<
      IApiPaginatedResponse<IPayrollPolicy[]>,
      IGetPayrollPoliciesParams | void
    >({
      query: (params) => ({
        url: "/company/settings/payroll/policies",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 10,
          // Filtering is the server's job: the list is paginated, so filtering a
          // received page would search only the rows that happen to be on it.
          ...(params?.search ? { search: params.search } : {}),
        },
      }),
      providesTags: ["payrollPolicies"],
    }),
    getPayrollPolicyById: builder.query<IApiResponse<IPayrollPolicy>, number>({
      query: (id) => ({
        url: `/company/settings/payroll/policies/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "payrollPolicy", id }],
    }),
    createPayrollPolicy: builder.mutation<
      IApiResponse<IPayrollPolicy>,
      ICreatePayrollPolicyRequest
    >({
      query: (body) => ({
        url: "/company/settings/payroll/policies",
        method: "POST",
        body,
      }),
      invalidatesTags: ["payrollPolicies"],
    }),
    updatePayrollPolicy: builder.mutation<
      IApiResponse<IPayrollPolicy>,
      { id: number; data: ICreatePayrollPolicyRequest }
    >({
      query: ({ id, data }) => ({
        url: `/company/settings/payroll/policies/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "payrollPolicies",
        { type: "payrollPolicy", id },
      ],
      onQueryStarted: syncPayrollReadiness,
    }),
    /**
     * Server-side TOGGLE — it flips `isActive` itself and ignores any body, so
     * a double submit would flip twice. Guard the control while in flight.
     */
    togglePayrollPolicyStatus: builder.mutation<IApiResponse<IPayrollPolicy>, number>({
      query: (id) => ({
        url: `/company/settings/payroll/policies/${id}/status`,
        method: "PATCH",
      }),
      invalidatesTags: (_r, _e, id) => [
        "payrollPolicies",
        { type: "payrollPolicy", id },
      ],
      onQueryStarted: syncPayrollReadiness,
    }),
    deletePayrollPolicy: builder.mutation<IApiResponse<null>, number>({
      query: (id) => ({
        url: `/company/settings/payroll/policies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["payrollPolicies"],
      onQueryStarted: syncPayrollReadiness,
    }),
  }),
});

export const {
  useGetPayrollPolicyUsageQuery,
  useAssignPolicyToBranchesMutation,
  useGetPayrollPoliciesQuery,
  useGetPayrollPolicyByIdQuery,
  useCreatePayrollPolicyMutation,
  useUpdatePayrollPolicyMutation,
  useTogglePayrollPolicyStatusMutation,
  useDeletePayrollPolicyMutation,
} = companyPayrollPoliciesApi;
